import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { useApiQuery, useApiMutation, queryClient } from "@/api/ApiQueryServiceProvider"
import { Link } from "@tanstack/react-router"
import { Search, Edit, Trash2, ChevronLeft, ChevronRight, Loader2, Plus, Building2, Navigation } from "lucide-react"
import type { NetworkSite, GetNetworkSitesResponse, GetNetworkCorridorsResponse } from "@/types/sensNet"
import { useToast } from "@/contexts/ToastContext"

/**
 * NetworkSiteList Component
 *
 * Displays a list of network sites with:
 * - Search functionality by site name
 * - Filter by network corridor
 * - Pagination (10 items per page)
 * - Add, Edit, and Delete actions via dialogs
 * - Displays site details: name, location (GPS), indoor/outdoor type, network technologies badges
 * - Loading and error states
 * - Toast notifications for mutations
 *
 * @returns {JSX.Element} The network sites list component
 */
export type NetworkSiteListProps = {
  onAdd?: () => void
  onEdit?: (site: NetworkSite) => void
}

export function NetworkSiteList({ onAdd, onEdit }: NetworkSiteListProps = {}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [corridorFilter, setCorridorFilter] = useState<string>("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedSite, setSelectedSite] = useState<NetworkSite | null>(null)
  const itemsPerPage = 10
  const { toast } = useToast()

  // Fetch network sites from API
  const { data, isLoading, isError, refetch } = useApiQuery<GetNetworkSitesResponse>(
    "getNetworkSites",
    {}
  )

  // Fetch corridors for filter dropdown
  const { data: corridorsData } = useApiQuery<GetNetworkCorridorsResponse>("getNetworkCorridors", {})

  // Map API response to component state
  // Backend returns networkSites with networkSiteNumber, networkSiteName, etc.
  const rawSites = (data as any)?.content?.networkSites || (data as any)?.networkSites || []
  const sites: NetworkSite[] = rawSites.map((s: any) => ({
    id: s.id,
    networkSiteNumber: s.networkSiteNumber,
    corridorId: s.corridorId || s.networkCorridorNumber?.toString() || '',
    name: s.name || s.networkSiteName || '',
    siteAddress: s.siteAddress || s.networkSiteAddress || '',
    siteGeoLocation: s.siteGeoLocation || s.location || { latitude: 0, longitude: 0 },
    locationNotes: s.locationNotes || '',
    indoorOrOutdoorSite: s.indoorOrOutdoorSite || 'outdoor',
    verticalStructureHeight: s.verticalStructureHeight,
    outdoorVerticalStructureType: s.outdoorVerticalStructureType,
    indoorStructureType: s.indoorStructureType,
    networkTechnologies: s.networkSiteTechnologies || s.networkTechnologies || [],
    backhaulCapability: s.backhaulCapability || 'fiber',
    shelterAvailable: s.shelterAvailable || false,
    cabinetAttached: s.cabinetAttached || false,
    sitePictures: s.sitePictures || [],
    networkSiteLaunchDate: s.networkSiteLaunchDate,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
  }))

  // Filter sites by search query and corridor
  const filteredSites = sites.filter((site) => {
    const matchesSearch = site.name.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesCorridor = corridorFilter === "all" || site.corridorId === corridorFilter

    return matchesSearch && matchesCorridor
  })

  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedSites = filteredSites.slice(startIndex, startIndex + itemsPerPage)

  const deleteSiteMutation = useApiMutation('deleteNetworkSite', 'DELETE', {
    onSuccess: () => {
      toast({
        title: "Site Deleted",
        description: `Network site "${selectedSite?.name}" has been deleted.`,
      });
      setIsDeleteDialogOpen(false);
      setSelectedSite(null);
      queryClient.invalidateQueries({ queryKey: ["api", "getNetworkSites"] });
    },
    onError: (error) => {
      toast({
        title: "Deletion Failed",
        description: `Failed to delete site: ${error.message}`,
        type: "error",
      });
      log.error("Delete site error:", error);
    },
  });

  const handleEdit = (site: NetworkSite) => {
    onEdit?.(site)
  }

  const handleDelete = (site: NetworkSite) => {
    setSelectedSite(site)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (selectedSite) {
      deleteSiteMutation.mutate({ networkSiteNumber: selectedSite.networkSiteNumber });
    }
  };

  // Format GPS location for display
  const formatGPSLocation = (geoLocation: NetworkSite['siteGeoLocation']) => {
    if (!geoLocation || !geoLocation.latitude || !geoLocation.longitude) {
      return 'No location'
    }
    return `${geoLocation.latitude.toFixed(6)}, ${geoLocation.longitude.toFixed(6)}`
  }

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load network sites</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Network Sites</CardTitle>
                <CardDescription className="text-base">Manage network sites and infrastructure</CardDescription>
              </div>
            </div>

            <Button onClick={() => onAdd?.()}>
              <Plus className="mr-2 h-4 w-4" /> Add Site
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search sites by name..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="pl-9"
                />
              </div>
              <div className="w-full sm:w-[200px]">
                <Select value={corridorFilter} onValueChange={(value) => {
                  setCorridorFilter(value)
                  setCurrentPage(1)
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by corridor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Corridors</SelectItem>
                    {((corridorsData as any)?.content?.networkCorridors || (corridorsData as any)?.networkCorridors || []).map((corridor: any) => (
                      <SelectItem key={String(corridor.networkCorridorNumber)} value={corridor.networkCorridorNumber.toString()}>
                        {corridor.networkCorridorName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
        {/* Table */}
        <Table className="text-base">
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="font-semibold text-base">Name</TableHead>
              <TableHead className="font-semibold text-base">Location</TableHead>
              <TableHead className="font-semibold text-base">Type</TableHead>
              <TableHead className="font-semibold text-base">Network Technologies</TableHead>
              <TableHead className="font-semibold text-base">Launch Date</TableHead>
              <TableHead className="w-[120px] text-right font-semibold text-base">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedSites.length > 0 ? (
              paginatedSites.map((site) => (
                <TableRow key={site.networkSiteNumber} className="transition-colors hover:bg-muted/50">
                  <TableCell className="font-medium text-base">
                    <Link
                      to="/sensnet/network-sites/$siteNumber"
                      params={{ siteNumber: site.networkSiteNumber ?? 0 }}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {site.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Navigation className="h-3 w-3 text-muted-foreground" />
                      <span className="text-base font-mono">{formatGPSLocation(site.siteGeoLocation)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={site.indoorOrOutdoorSite === 'indoor' ? 'secondary' : 'default'}>
                      {site.indoorOrOutdoorSite === 'indoor' ? 'Indoor' : 'Outdoor'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      {site.networkTechnologies && site.networkTechnologies.length > 0 ? (
                        site.networkTechnologies.map((tech) => {
                          const techName = typeof tech === 'string' ? tech : tech.technology
                          return (
                            <Badge key={techName} variant="outline">
                              {techName}
                            </Badge>
                          )
                        })
                      ) : (
                        <span className="text-base text-muted-foreground">None</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {site.networkSiteLaunchDate
                      ? new Date(site.networkSiteLaunchDate * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                      >
                        <Link
                          to="/sensnet/network-sites/$networkSiteNumber/edit"
                          params={{ networkSiteNumber: String(site.networkSiteNumber) }}
                        >
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit {site.name}</span>
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handleDelete(site)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete {site.name}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  {searchQuery || corridorFilter !== "all"
                    ? "No network sites found matching your filters."
                    : "No network sites found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {filteredSites.length > itemsPerPage && (
          <div className="flex items-center justify-between border-t py-3">
            <p className="text-base text-muted-foreground">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + itemsPerPage, filteredSites.length)} of{" "}
              {filteredSites.length} sites
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">Page {currentPage} of {Math.ceil(filteredSites.length / itemsPerPage)}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage >= Math.ceil(filteredSites.length / itemsPerPage)}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Network Site</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the network site "<strong>{selectedSite?.name}</strong>"?
              <br />
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setSelectedSite(null);
              }}
              disabled={deleteSiteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteSiteMutation.isPending}
            >
              {deleteSiteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
